import { useCallback } from 'react'

import type { ReleaseForm, TaggableUser } from '@api'
import times from 'lodash/times'
import { type ListChildComponentProps, VariableSizeList } from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content'
import { TextField } from '@/components/ui/text-field'
import { useInfiniteScroll } from '@/dialogs/TagOtherCreatorsAndReleaseFormsDialog/useInfiniteScroll.ts'
import { ManageSelectedUsers } from '@/modules/chatting/ui/components/ManageSelectedUsers.tsx'
import { FilterSortOrderOptions } from '@/modules/chatting/ui/components/filter-sort-order-options.tsx'

import { getTaggableItemsListItemSize } from '../utils'
import { ReleaseFormItem } from './ReleaseFormItem'
import { ReleaseFormSkeleton } from './ReleaseFormSkeleton'
import { useReleaseFormsData } from './useReleaseFormsData'

interface Props {
  onClose: () => void
  onClickDone: () => void
  selectedReleaseForms: ReleaseForm[]
  selectedCreators: TaggableUser[]
  onChangeReleaseFormSelection: (
    releaseForm: ReleaseForm,
    state: boolean,
  ) => void
  onResetSelection: () => void
}

export const ReleaseFormTabContent = ({
  onClose,
  onClickDone,
  selectedReleaseForms,
  onChangeReleaseFormSelection,
  onResetSelection,
  selectedCreators,
}: Props) => {
  const {
    releaseForms,
    isLoading,
    handleSearchChange,
    filterSortOrderOptions,
    canLoadMore,
    onLoadMore,
    isLoadingMore,
  } = useReleaseFormsData()

  const { lastVisibleIndexRef, container, onScroll } = useInfiniteScroll({
    canLoadMore,
    onLoadMore,
    entities: releaseForms,
  })

  const handleClickDone = () => {
    onClickDone()
    onClose()
  }

  const renderReleaseFormItem: React.ComponentType<
    ListChildComponentProps<ReleaseForm[]>
  > = useCallback(
    ({ data, index, style }) => {
      const releaseForm = data[index]
      const isSelected = !!selectedReleaseForms.find(
        ({ id }) => id === releaseForm.id,
      )

      lastVisibleIndexRef.current = index

      return (
        <ReleaseFormItem
          onChangeSelected={(state) =>
            onChangeReleaseFormSelection(releaseForm, state)
          }
          isSelected={isSelected}
          releaseForm={releaseForm}
          style={style}
        />
      )
    },
    [lastVisibleIndexRef, onChangeReleaseFormSelection, selectedReleaseForms],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <TextField
          placeholder="Search"
          className="border-border-secondary flex-1 resize-none"
          prefix={<SearchIcon className="text-title" />}
          onChange={handleSearchChange}
        />
        <FilterSortOrderOptions {...filterSortOrderOptions} />
      </div>
      <div
        className="no-scrollbar border-border-secondary bg-card relative max-h-[532px] w-full flex-1 overflow-hidden rounded-lg border py-4"
        ref={container.ref}
      >
        {isLoading ? (
          times(6).map((num) => <ReleaseFormSkeleton key={num} />)
        ) : releaseForms.length === 0 ? (
          <EmptyTableContent>
            No results found. <br />
            Please change filter criteria.
          </EmptyTableContent>
        ) : (
          <VariableSizeList
            className="scrollbar-hide min-h-[498px] max-w-full overflow-hidden"
            itemData={releaseForms}
            itemSize={getTaggableItemsListItemSize}
            height={container.height}
            itemCount={releaseForms?.length ?? 0}
            width={container.width}
            onScroll={onScroll}
          >
            {renderReleaseFormItem}
          </VariableSizeList>
        )}
        <div className="bg-card absolute bottom-0 left-0 right-0 space-y-4">
          <ManageSelectedUsers
            className="mx-4 mb-4"
            onCancel={onResetSelection}
            selectedCount={
              selectedCreators.length + selectedReleaseForms.length
            }
          />
          {isLoadingMore && (
            <div>
              <IconLoading className="mx-auto my-3 animate-spin" />
            </div>
          )}
        </div>
      </div>
      <Dialog.ClosableFooter
        className="flex"
        onClose={onClose}
        buttonClassName="flex-1 min-w-max"
      >
        <Button
          onClick={handleClickDone}
          className="min-w-max flex-1"
        >
          Done
        </Button>
      </Dialog.ClosableFooter>
    </div>
  )
}
